/*
 * Copyright (c) 2025 BuyerView.ai
 * All rights reserved.
 * Unauthorized copying prohibited.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */


import { useState, useEffect, useCallback } from 'react';
import { pwaService, type PWAState } from '../services/pwaService';

export function usePWA() {
  const [state, setState] = useState<PWAState>(pwaService.getState());

  useEffect(() => {
    // Initialize PWA service
    pwaService.initialize();

    // Set up event listeners
    const handleInstallation = (newState: PWAState) => {
      setState(prev => ({ ...prev, isInstalled: newState.isInstalled, canInstall: newState.canInstall }));
    };

    const handleUpdate = (newState: PWAState) => {
      setState(prev => ({ ...prev, updateAvailable: newState.updateAvailable }));
    };

    const handleOnline = (newState: PWAState) => {
      setState(prev => ({ ...prev, isOffline: newState.isOffline }));
    };

    const handleInstall = (newState: PWAState) => {
      setState(prev => ({ ...prev, canInstall: newState.canInstall, installPrompt: newState.installPrompt }));
    };

    const handleSync = (_newState: PWAState) => {
      // Handle background sync completion
      setState(prev => ({ ...prev }));
    };

    pwaService.addEventListener('installation', handleInstallation);
    pwaService.addEventListener('update', handleUpdate);
    pwaService.addEventListener('online', handleOnline);
    pwaService.addEventListener('install', handleInstall);
    pwaService.addEventListener('sync', handleSync);

    // Update initial state
    setState(pwaService.getState());

    return () => {
      pwaService.removeEventListener('installation', handleInstallation);
      pwaService.removeEventListener('update', handleUpdate);
      pwaService.removeEventListener('online', handleOnline);
      pwaService.removeEventListener('install', handleInstall);
      pwaService.removeEventListener('sync', handleSync);
    };
  }, []);

  // Install app
  const installApp = useCallback(async () => {
    try {
      const result = await pwaService.showInstallPrompt();
      return result;
    } catch (error) {
      console.error('Failed to install app:', error);
      throw error;
    }
  }, []);

  // Update app
  const updateApp = useCallback(async () => {
    try {
      await pwaService.updateServiceWorker();
    } catch (error) {
      console.error('Failed to update app:', error);
      throw error;
    }
  }, []);

  // Store offline action
  const storeOfflineAction = useCallback(async (type: 'review' | 'image' | 'user_action', data: any) => {
    try {
      const id = await pwaService.storeOfflineAction({ type, data });
      return id;
    } catch (error) {
      console.error('Failed to store offline action:', error);
      throw error;
    }
  }, []);

  // Request notification permission
  const requestNotifications = useCallback(async () => {
    try {
      const permission = await pwaService.requestNotificationPermission();
      return permission;
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      throw error;
    }
  }, []);

  // Subscribe to push notifications
  const subscribeToNotifications = useCallback(async (vapidKey: string) => {
    try {
      const subscription = await pwaService.subscribeToPushNotifications(vapidKey);
      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to notifications:', error);
      throw error;
    }
  }, []);

  return {
    ...state,
    installApp,
    updateApp,
    storeOfflineAction,
    requestNotifications,
    subscribeToNotifications
  };
}